import {customElement, bindable, autoinject, computedFrom} from "aurelia-framework";
import {factory} from 'aurelia-dependency-injection';
import {bindingMode as BindingMode} from "aurelia-binding";
import {IFormSchema, IFormPropertySchema} from "../../../services/formSchema/form-schema-service";
import {DisposableCollection} from "../../../services/disposable-collection";
import {AureliaHelperService} from "../../../services/aurelia-helper-service";
import {ISortColumnService} from "../../../services/sort-column-service"
import {ISelectOption} from "../../../interfaces/ISelectOption";
import {IFormSchemaCollectionInstanceApi, ValidateResult} from "../../../services/formSchema/form-schema-collection-controller";
import {IDisposable} from "../../../interfaces/IDisposable";
import { validateTrigger as ValidateTrigger, ValidationController} from "aurelia-validation";
import {IFormSchemaCollectionValidationRenderer, FormSchemaCollectionValidationRenderer } from "./form-schema-collection-validation-renderer";
import {IValidationEventAggregator, ValidationEventAggregator } from "../../../services/validation/validation-event-aggregator";
import { IValidationService, IValidationRenderer } from "../../../services/validation/validation-service";
import "./form-schema-collection.css";
import { JavaScriptService } from "../../../services/javascript-service";
import { EditableInlineDropdownCheckbox } from "../editable-inline-dropdown-checkbox/editable-inline-dropdown-checkbox";
import { EditableInlineSelect } from "../editable-inline-select/editable-inline-select";

@autoinject
@customElement('form-schema-collection')
export class FormSchemaCollection {

    /** 
     * callback after new instances array is processed
     */
    @bindable({ defaultBindingMode: BindingMode.oneWay })
    onNewInstances?: (instances: Array<any>) => void;

    /**
        insert will be invoked by 'click'
    **/
    @bindable({ defaultBindingMode: BindingMode.oneWay })
    externalInsertButton: Element|null = null;

    @bindable({ defaultBindingMode: BindingMode.oneWay })
    tableClass: string|null = null;

    @bindable({ defaultBindingMode: BindingMode.oneWay })
    controller: IFormSchemaCollectionInstanceApi<any>;

    theTable: Element;
    formSchema: IFormSchema;
    handleDelete: ((instance: any) => Promise<boolean>) | undefined;
    handleNew: ((instance: any) => Promise<boolean>) | undefined;
    handleCreate: (() => Promise<any>) | undefined;
    handleUpdate: ((instance: any, propertyName: string, newValue: any, oldValue: any) => Promise<boolean>) | undefined;
    sortColumnService: ISortColumnService;
    properties: Array<IFormPropertySchema>;
    renderers: Array<IValidationRenderer> = [];
    validationService?: IValidationService;
    validationController?: ValidationController;
    /**
     * true to insert to the database immediately on clicking New
     * false to provide save/cancel buttons.  Note that validation-error "revert" button is not provided in this case
     */
    insertOnNew: boolean = false;

    @computedFrom("controller.disableNew", "externalInsertButton")
    get disableNew(): boolean {
        return !!this.controller.disableNew || !!this.externalInsertButton;
    }

    @computedFrom("controller.disableDelete")
    get disableDelete(): boolean {
        return !!this.controller.disableDelete;
    }

    @computedFrom("controller.showKeyColumn")
    get showKeyColumn(): boolean {
        return !!this.controller.showKeyColumn;
    }

    private binding: boolean = false;

    subscriptions: DisposableCollection = new DisposableCollection();

    disableSorting() {
        this.sortColumnService.sortEnabled = false;
    }

    enableSorting() {
        setTimeout(() => { this.sortColumnService.sortEnabled = true; },0);
    }

    constructor(
        private aureliaHelperService: AureliaHelperService,
        private javaScriptService: JavaScriptService
    ) {
    }

    private instances: Array<IWatchedInstance> = [];

    private setInstances(value: Array<any>): void {

        /**
         * disable sorting on load to handle case where we are returning to the view after having left it with invalid
         * fields whose temporary values could case sort order to change.
         *
         * This can go away when aurelia publishes the ability to cache views.
         * But note that it requires the server to do the correct sorting.
         *
         * this.sortColumnService.currentSortColumn isn't available yet at this point
         */
        this.disableSorting();

        this.clearInstances();
        for (let instance of value) {
            this.initInstance(instance);
            this.javaScriptService.yield(); // give UI a timeslice
        }
        this.instances = value;
        
        this.enableSorting();

        if (this.onNewInstances) {
            this.onNewInstances(this.instances);
        }
    }

    private clearInstances(): void {
        if (this.instances) {
            for (let instance of this.instances) {
                this.unInitInstance(instance);
            }
            this.instances = [];
        }
    }

    bind() {
        /**
         * what this means is that the controller must not be loaded until this control has been bound
         */
        this.subscriptions.push(this.controller.subscribe('loaded', () => { this.initializeOnLoad(); }));
        this.subscriptions.push(this.controller.subscribe('refetched', () => { this.setInstances(this.controller.instances); }));
    }

    unbind() {

        if (this.instances.length) {
            this.clearInstances()
            this.subscriptions.dispose();
            if (this.validationService) {
                // have to do this or things won't work right when switching between views
                for (let renderer of this.renderers) {
                    this.validationService.removeValidationRenderer(renderer);
                }
                this.renderers = [];
            }
        }
    }

    private initializeOnLoad() {

        this.sortColumnService = this.controller.sortColumnService;

        this.formSchema = this.controller.formSchema;

        this.properties = this.formSchema.properties
            .filter((p) => { return !p.isKey || this.showKeyColumn });

        if (this.externalInsertButton) {
            $(this.externalInsertButton).on('click.formSchemaCollection', () => {
                this.createInstance();
            });
        }

        this.handleDelete = this.controller.handleDelete;
        this.handleNew = this.controller.handleNew;
        this.handleCreate = this.controller.handleCreate;
        this.handleUpdate = this.controller.handleUpdate;
        this.insertOnNew = this.controller.insertOnNew;
        /**
         * Important to remember here:  This entire viewmodel is reinstantiated every time the containing view is bound!
         * Setting/unsetting the renderer each time, and revalidating, should no longer be necessary
         * when aurelia publishes the ability to cache views.
         */
        let renderer = this.aureliaHelperService.getInstanceOf(FormSchemaCollectionValidationRenderer) as IFormSchemaCollectionValidationRenderer;
        this.renderers.push(renderer);

        let validationEventRenderer = this.aureliaHelperService.getInstanceOf(ValidationEventAggregator) as IValidationEventAggregator;
        this.renderers.push(validationEventRenderer);

        this.validationService = this.controller.validationService;

        if (this.validationService) {
            // TODO:  the HTML references this.  Probably will complain if not defined
            this.validationController = this.validationService.validationController;
            this.validationService.validateTrigger = ValidateTrigger.manual;

            this.validationService.addValidationRenderer(renderer);
            
            this.validationService.addValidationRenderer(validationEventRenderer);

            this.subscriptions.push(validationEventRenderer.subscribe("validationChanged",
                (parms: any) => { this.handleValidationChanged(parms.instance, parms.errors); }));
        }

        if (this.controller.instances) {
            this.setInstances(this.controller.instances);

            // won't work without timeout
            // TODO: this is really slow when there are a lot of things to validate (like the Vivalia Edit Products page)
            setTimeout(() => this.controller.revalidateAll() , 0);
        } // else there was an error fetching the data
    }

    initInstance(instance: IWatchedInstance) {
        this.watchPropertyValues(instance);
        this.controller.registerInstances([instance]);
        // todo: somehow indicate to auralia what are the dependencies here so it won't poll
        //this.aureliaHelperService.createObservableComputedProperty(instance, "_validationHtml",
        //    () => {
        //        return this.getValidationMessageHtml(instance, this.validationService.validationMessagesForInstance(instance));
        //    });
    }

    unInitInstance(instance: IWatchedInstance) {
        this.unwatchPropertyValues(instance);
        this.controller.unRegisterInstances([instance]);
    }

    unwatchPropertyValues(instance: IWatchedInstance): void {
        instance._propertyChangeSubscriptions.emptyAndDispose();
        this.controller.unRegisterInstances([instance]);
    }

    watchPropertyValues(instance: IWatchedInstance): void {

        if (!instance._propertyChangeSubscriptions) {
            instance._propertyChangeSubscriptions = new DisposableCollection();
        } else {
            this.unwatchPropertyValues(instance);
        }

        for (let propertySchema of this.properties) {
            instance._propertyChangeSubscriptions.push(this.aureliaHelperService.createPropertyWatch(instance,
                propertySchema.name,
                (newValue, oldValue) => {

                    if (!instance._pendingInsert) {
                        this.updateInstance(instance, propertySchema.name, newValue, oldValue);
                    } else {
                        this.controller.validate(instance, propertySchema.name, newValue, oldValue);
                    }
                }));
        }
    }

    deleteInstance(instance: IWatchedInstance) {
        this.controller.handleDelete(instance)
            .then((ok: boolean) => {
                if (ok) {
                    this.deleteRow(instance);
                }
            });
    }

    /**
     * get blank instance without adding to DB
     */
    createInstance() {
        this.controller.handleCreate()
            .then((instance: any) => {
                if (instance) {

                    if (this.insertOnNew) {
                        this.saveNewInstance(instance);
                    } else {
                        instance._pendingInsert = true;
                        this.newRow(instance);
                    }
                }
            });
    }

    saveNewInstance(instance: IWatchedInstance) {
        this.controller.handleNew(instance)
            .then((newInstance: any) => {
                if (newInstance) {

                    if (instance._pendingInsert) {
                        instance._pendingInsert = false;
                        // keep new row in it's current position, but allow it to be sorted thereafter
                        instance._ordinalPosition = undefined;
                        this.disableSorting();
                        // to get primary key and whatever else the server wants to send back
                        this.replaceRow(instance, newInstance);
                        this.enableSorting();
                    } else {
                        newInstance._newlyInserted = true;
                        // keep new row in it's current position, not allowing it to be sorted.  It still requires editing.
                        this.newRow(newInstance);
                    }

                    setTimeout(() => { this.animateRowChanged(newInstance); }, 0);
                }
            });
    }

    cancelNewInstance(instance: IWatchedInstance) {
        this.deleteRow(instance);
    }

    updateInstance(instance: IWatchedInstance, propertyName: string, newValue: any, oldValue: any): void {

        this.controller.handleUpdate(instance,propertyName, newValue, oldValue)
            .then((success) => {
                if (success) {
                    this.animateRowChanged(instance);
                }
            });
    }

    deleteRow(instance: IWatchedInstance) {

        this.unInitInstance(instance);

        let ndx = this.instances.findIndex((i) => { return i === instance });

        if (ndx !== -1) {
            this.instances.splice(ndx, 1);
        }

        if (instance._ordinalPosition !== undefined) {
            this.normalizeOrdinalPositions();
        }
    }

    newRow(instance: any) {
        this.initInstance(instance);

        let hardCodedInstances = this.instances.filter((i) => { return i._ordinalPosition !== undefined; });

        for (let instance of hardCodedInstances) {
            instance._ordinalPosition = <number>instance._ordinalPosition + 1;
        }

        // will appear at top and won't be subject to sorting.
        instance._ordinalPosition = 0;

        this.instances.splice(0, 0, instance); 
    }

    replaceRow(oldInstance: any, newInstance: any) {

        let ndx = this.instances.findIndex((i) => { return i === oldInstance });

        this.unInitInstance(oldInstance);

        this.initInstance(newInstance);

        newInstance._ordinalPosition = oldInstance._ordinalPosition;

        this.instances.splice(ndx, 1, newInstance); 
    }

    handleValidationChanged(instance: IWatchedInstance, errors: Array<ValidateResult>) {
        instance._validationHtml = this.getValidationMessageHtml(instance, errors);
    }

    animationDuration: number = 2;

    animateRowChanged(instance: IWatchedInstance) {
        let theRow = $(this.theTable).find("#" + instance[this.formSchema.key]);

        theRow.css("animation-name", "none");

        setTimeout(() => {
            theRow.css("animation-name", 'animateOnUpdate');
            // so row doesn't reanimate when sorting the table
            setTimeout(() => {
                    theRow.css("animation-name", "none");
                },
                this.animationDuration * 1000 + .5);
        }, 10);
    }

    /**
     * for when a hardcoded instance was deleted
     */
    private normalizeOrdinalPositions() {

        let hardCodedInstances = this.instances.filter((i) => { return i._ordinalPosition !== undefined; })
            .sort((a, b) => { return (<number>a._ordinalPosition - <number>b._ordinalPosition); });

        let ndx = 0;

        for (let instance of hardCodedInstances) {
            instance._ordinalPosition = ndx++;
        }
    }

    public revert(ndx:number):void {

        let instance: IWatchedInstance = this.instances[ndx];

        this.controller.fetchInstance(instance)
            .then((freshInstance: IWatchedInstance) => {
                this.replaceRow(instance, freshInstance);
            });
    }

    private getValidationMessageHtml(instance: IWatchedInstance, errors: Array<ValidateResult>): string|undefined {

        let html: string|undefined;

        if (errors && errors.length) {
            // include popover-template to enable popover to intercept our mouse clicks.
            html = "<div id='validationMessagesPopup'><div class='validationMessagesPopup'>";

            errors.forEach((error: ValidateResult) => {
                html += `<div class='message'>${error.message}</div>`;
            });

            if (!instance._pendingInsert) { // don't have anything to revert-to (if aren't persisting until Save)
                html += `<div class='revert'><button class='btn btn-xs btn-primary' click.delegate='revert(${this.instances.indexOf(instance)})'>Revert</button></div>`;
            }

            html += "</div></div>";
        }

        return html;
    }

    private dropdownTextFromValue(items: Array<ISelectOption>, value: number | string): string {
        return EditableInlineSelect.dropdownTextFromValue(items, value);
    }

    private computeSelectedItemsString(items: Array<ISelectOption>, selectedItems: Array<number | string>): string {
        return EditableInlineDropdownCheckbox.computeSelectedItemsString(items, selectedItems);
    }
}

export interface IWatchedInstance  {
    _propertyChangeSubscriptions: DisposableCollection;
    _pendingInsert: boolean;
    _newlyInserted: boolean;
    _ordinalPosition: number|undefined;
    _validationHtml: string|undefined;
    [number: string]: any;
}
